import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { createPatient } from 'api/Patients';

import { formatDate, randomString } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import DatePopover from 'components/shared/Popovers/Date/DatePopover';

import IdleWrapper from '../components/IdleWrapper';
import RequestAssistance from '../components/RequestAssistance';

const RegisterPatient = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [initialData, setInitialData] = useState({
    firstName: '',
    lastName: '',
    date: new Date(),
    email: '',
    noEmail: false
  });

  useEffect(() => {
    const patientData = location.state?.patientData;

    if (patientData) {
      setInitialData((prevData) => ({
        ...prevData,
        firstName: patientData.firstName || '',
        lastName: patientData.lastName || '',
        date: patientData.date ? new Date(patientData.date) : new Date()
      }));
    }
  }, [location.state]);

  const formik = useFormik({
    initialValues: initialData,
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      date: Yup.date().required('Date of birth is required'),
      email: Yup.string().when('noEmail', {
        is: false,
        then: () => Yup.string().email('Invalid email address').required('Email is required'),
        otherwise: () => Yup.string().notRequired()
      })
    }),
    onSubmit: async (values) => {
      setLoading(true);

      const submitValues = {
        patient: {
          f_name: values.firstName,
          l_name: values.lastName,
          dob: formatDate(values.date),
          email: values.noEmail ? `autogen_${randomString(16)}@gen.myriad.health` : values.email
        },
        invitationEmail: values.noEmail ? false : true
      };

      const response = await createPatient(navigate, submitValues);

      if (
        response?.patient &&
        response?.code === 0 &&
        (response.patient?.id || response.patientId)
      ) {
        showAlert({
          title: 'Patient created successfully',
          message: !response?.emailSent ? 'However, the invitation email could not be sent.' : null,
          color: !response?.emailSent ? 'warning' : 'success'
        });
        navigate('../schedule-appointment', {
          state: {
            patientId: response.patient?.id || response.patientId,
            patientName: `${values.firstName} ${values.lastName}`
          }
        });
      } else if (response?.code === 3) {
        showAlert({
          title: 'Patient duplication!',
          message: 'A patient with this email already exists in your practice.',
          color: 'danger',
          icon: 'new-user-2'
        });
      } else if (response?.code === 4) {
        showAlert({
          title: 'Email is not valid!',
          message: 'Please enter a valid email address.',
          color: 'danger',
          icon: 'mail-mono'
        });
      } else {
        showAlert({ title: 'Failed to create patient', color: 'danger' });
      }

      setLoading(false);
    }
  });

  return (
    <IdleWrapper>
      <div className="rounded-t-2xl bg-primary-50 !px-8 !py-3 xs:!px-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-lg font-600 leading-7 text-primary-900">Patient Registration</p>
          <div
            className="ml-auto flex cursor-pointer select-none gap-1"
            onClick={() => navigate('../idle')}>
            <Icon
              size={20}
              stroke
              color="primary"
              shade={900}
              icon="new-arrow-left"
              className="cursor-pointer"
            />
            <p className="text-base font-600 leading-7 text-primary-900">Go back</p>
          </div>
        </div>
        <p className="font-regular mt-1 text-base text-neutral-600">
          Please complete the registration form below to create your patient account
        </p>
      </div>
      <div className="!px-8 !py-4 xs:!px-4 xs:!pb-3 xs:!pt-2">
        <div className="mb-4 grid grid-cols-1">
          <div className="mb-4 grid grid-cols-3 gap-4 xs:grid-cols-1">
            <Input
              label="First Name"
              name="firstName"
              placeholder="Enter first name"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={formik.errors.firstName}
              formik={formik}
              validateName
            />
            <Input
              label="Last Name"
              name="lastName"
              placeholder="Enter last name"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.errors.lastName}
              formik={formik}
              validateName
            />
            <DatePopover
              label="Date of Birth"
              placeholder="mm/dd/yyyy"
              name="date"
              value={formik.values.date ? new Date(formik.values.date) : null}
              onChange={(date) => {
                formik.setFieldValue('date', date);
              }}
              error={formik.errors.date}
              formik={formik}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 xs:grid-cols-1">
            <Input
              label="Email Address"
              name="email"
              type="email"
              placeholder="Enter your email address"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.errors.email}
              formik={formik}
              disabled={formik.values.noEmail}
            />
            <div className="mt-9 flex items-start">
              <Checkbox
                name="noEmail"
                label="I don't have an email"
                checked={formik.values.noEmail}
                onChange={() => {
                  formik.setFieldValue('noEmail', !formik.values.noEmail);
                }}
                className="mb-2"
              />
            </div>
          </div>
        </div>

        <Button
          size="large"
          text="Register"
          className="!mt-4 w-full"
          loading={loading}
          onClick={() => formik.submitForm()}
        />

        <div className="!mt-4 flex select-none flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <RequestAssistance page="patient-registration" />
          <div className="ml-auto flex items-center gap-x-[6px]">
            <p className="text-xs font-400 text-primary-500">Powered by</p>
            <Icon icon="logo-without-symbol" size={76} />
          </div>
        </div>
      </div>
    </IdleWrapper>
  );
};

export default RegisterPatient;
